import { useState } from "react";
import { useTranslation } from "react-i18next";

import { SHORT_DATE_FORMAT } from "shared/constants";
import { formatDate } from "shared/locale/format";

import Dialog from "features/components/Dialog";

import Image from "./Image";
import ImageButtons from "./ImageButtons";
import ThumbnailList from "./ThumbnailList";
import { GalleryPhoto } from "./types";
import { isVideoMimeType } from "./utils";
import Video from "./Video";

interface Props {
  images?: GalleryPhoto[];
  open: boolean;
  onClose: () => void;
  title?: string | null;
  initialSelectedImageId?: string;
  header?: JSX.Element;
  hideImageNavigation?: boolean;
}

const ImagesModal = ({
  images,
  open,
  onClose,
  title,
  initialSelectedImageId,
  header,
  hideImageNavigation = false,
}: Props) => {
  const { t } = useTranslation("common");
  const [selectedImageId, setSelectedImageId] = useState<string | undefined>(
    initialSelectedImageId ?? images?.[0]?.ID
  );

  // Replacing a slot image drops the selected id out of the list while the viewer
  // is open. Without this fallback the dialog renders nothing and the parent still
  // thinks it is open, so clicking another image cannot bring it back.
  const selectedImage =
    images?.find(({ ID }) => ID === selectedImageId) ?? images?.[0];

  if (!images || !selectedImage) {
    return null;
  }

  return (
    <Dialog
      open={open}
      maxWidth="xl"
      fullWidth
      onClose={onClose}
      // A captioned image names its own source and follows the carousel, so it
      // outranks the gallery-wide title once the viewer is open. Wrapped rather
      // than passed through, since a caption may be any node.
      title={
        header ??
        (selectedImage.caption ? (
          <>{selectedImage.caption}</>
        ) : (
          (title ?? t("images.photos"))
        ))
      }
      showCloseIcon
      // A fixed-height paper + non-scrolling flex content lets the image area
      // take exactly the space left over after the title and thumbnails, so
      // object-contain can fit any photo without the dialog overflowing.
      slotProps={{ paper: { sx: { height: "calc(100% - 64px)" } } }}
      contentSx={{
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <div className="flex flex-col flex-1 min-h-0 -mx-6 -mb-5">
        <div className="flex-1 min-h-0 bg-slate-100 px-6 flex items-center justify-center">
          <div className="relative w-full h-full max-h-full">
            {isVideoMimeType(selectedImage) &&
            selectedImage.srcExpire &&
            selectedImage.srcUrl ? (
              <Video
                photoID={selectedImage.ID}
                srcExpire={selectedImage.srcExpire}
                srcUrl={selectedImage.srcUrl}
              />
            ) : (
              <Image
                photoId={selectedImage.ID}
                photoSrc={selectedImage.srcUrl ?? ""}
              />
            )}

            {!hideImageNavigation && (
              <div className="absolute right-0 bottom-4 z-10">
                <ImageButtons
                  images={images}
                  selectedImageId={selectedImage.ID}
                  setSelectedImageId={setSelectedImageId}
                />
              </div>
            )}
            {selectedImage?.uploadedAt && (
              <div className="absolute z-10 bottom-4 left-0 px-2 py-1 bg-gray-50/50 rounded-md">
                {formatDate(selectedImage.uploadedAt, SHORT_DATE_FORMAT)}
              </div>
            )}
          </div>
        </div>
        {!hideImageNavigation && (
          <ThumbnailList
            thumbnails={images
              .filter((t) => t.srcThumbnail)
              .map(({ ID, srcThumbnail }) => ({
                url: srcThumbnail!,
                ID,
              }))}
            selectedId={selectedImage.ID}
            onClick={setSelectedImageId}
          />
        )}
      </div>
    </Dialog>
  );
};

export default ImagesModal;
